import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { FileText, Receipt, Building2, UtensilsCrossed, CheckCircle2, Clock, Shield, Sparkles } from "lucide-react";

const ICON_MAP = { FileText, Receipt, Building2, UtensilsCrossed };

// Small words to keep lowercase in Title Case, except when they're the first word.
const _MINOR = new Set(["a","an","and","as","at","but","by","for","in","of","on","or","the","to","vs","via","with"]);

function toTitleCase(s) {
  if (!s) return "";
  return String(s).split(/(\s+)/).map((part, i) => {
    if (/^\s+$/.test(part)) return part;
    // Preserve any chunk that already contains an ALL-CAPS acronym (e.g. "GST",
    // "(ITR-3/4)", "F&O") so acronyms and codes stay uppercase.
    if (/[A-Z]{2,}/.test(part)) return part;
    const low = part.toLowerCase();
    if (i !== 0 && _MINOR.has(low)) return low;
    return low.charAt(0).toUpperCase() + low.slice(1);
  }).join("");
}

function pickDefaultPlan(plans) {
  if (!plans || plans.length === 0) return null;
  return plans.find((p) => p.popular) || plans[0];
}

function isValidityPlan(p) {
  return p && (p.pricing_type === "validity" || (p.year_prices && Object.keys(p.year_prices).length > 0));
}

function availableYears(p) {
  const yp = (p && p.year_prices) || {};
  return [1, 2, 3, 4, 5].filter((y) => Number(yp[y] ?? yp[String(y)] ?? 0) > 0);
}

function priceForPlan(p, year) {
  if (!p) return 0;
  if (isValidityPlan(p)) {
    const yp = p.year_prices || {};
    return Number(yp[year] ?? yp[String(year)] ?? p.price ?? 0);
  }
  return Number(p.price || 0);
}

function YearRadios({ plan, year, onChange, testidPrefix }) {
  const years = availableYears(plan);
  if (years.length === 0) return null;
  return (
    <div className="mt-3" data-testid={`${testidPrefix}-${plan.code}`}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Select year</div>
      <div className="flex flex-wrap gap-2">
        {years.map((y) => {
          const price = priceForPlan(plan, y);
          const active = year === y;
          return (
            <label
              key={y}
              className={`cursor-pointer px-3 py-2 rounded-md border text-sm font-semibold flex items-center gap-2 transition-colors ${
                active ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-white text-slate-700 border-slate-300 hover:border-[#1E3A8A]"
              }`}
              data-testid={`${testidPrefix}-${plan.code}-${y}`}
            >
              <input
                type="radio"
                className="accent-[#1E3A8A]"
                checked={active}
                onChange={() => onChange(y)}
              />
              Year {y}
              <span className={active ? "text-white" : "text-[#0A192F]"}>· ₹{price.toLocaleString("en-IN")}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

export default function ServiceDetail() {
  const { slug } = useParams();
  const [svc, setSvc] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [selectedCode, setSelectedCode] = useState(null);
  const [selectedYear, setSelectedYear] = useState(1);
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    api.get(`/services/${slug}`).then((r) => {
      const s = r.data.service;
      setSvc(s);
      const def = pickDefaultPlan(s.plans);
      if (def) {
        setSelectedCode(def.code);
        if (isValidityPlan(def)) {
          const ys = availableYears(def);
          setSelectedYear(ys[0] || 1);
        }
      }
    }).catch(() => setNotFound(true));
  }, [slug]);

  const sortedPlans = useMemo(() => {
    if (!svc?.plans) return [];
    return [...svc.plans];
  }, [svc]);

  if (notFound) {
    return (
      <PublicLayout>
        <div className="max-w-3xl mx-auto py-24 text-center">
          <h1 className="font-display font-bold text-3xl">Service not found</h1>
          <Link to="/services" className="text-[#1E3A8A] mt-4 inline-block">← Back to services</Link>
        </div>
      </PublicLayout>
    );
  }
  if (!svc) return <PublicLayout><div className="p-10 text-slate-500">Loading…</div></PublicLayout>;

  const Icon = ICON_MAP[svc.icon] || FileText;
  const hasPlans = sortedPlans.length > 0;
  const activePlan = hasPlans ? sortedPlans.find((p) => p.code === selectedCode) || sortedPlans[0] : null;
  const activeIsValidity = isValidityPlan(activePlan);
  const qtyEnabled = !!(activePlan && activePlan.quantity_enabled);
  const effectiveQty = qtyEnabled ? Math.max(1, Math.min(99, Number(quantity) || 1)) : 1;
  const unitPrice = activePlan ? priceForPlan(activePlan, selectedYear) : svc.price;
  const totalPrice = unitPrice * effectiveQty;
  const applyHref = (() => {
    if (!hasPlans || !activePlan) return `/apply/${svc.slug}`;
    const parts = [activePlan.code];
    if (activeIsValidity) parts.push(String(selectedYear));
    if (qtyEnabled && effectiveQty > 1) parts.push(`q${effectiveQty}`);
    return `/apply/${svc.slug}?plan=${parts.join(":")}`;
  })();

  const pickPlan = (p) => {
    setSelectedCode(p.code);
    setQuantity(1);
    if (isValidityPlan(p)) {
      const ys = availableYears(p);
      setSelectedYear(ys[0] || 1);
    }
  };

  return (
    <PublicLayout>
      <section className="gradient-radial-blue">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid lg:grid-cols-12 gap-10 items-start">
          {/* LEFT COLUMN */}
          <div className="lg:col-span-8">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-white border border-slate-200 text-[#1E3A8A] flex items-center justify-center">
                <Icon className="w-5 h-5" />
              </div>
              <span className="section-label">Service</span>
            </div>
            <h1 className="mt-4 font-display font-bold text-4xl sm:text-5xl text-[#0A192F]" data-testid="svc-detail-name">{svc.name}</h1>
            <p className="mt-4 text-slate-600 text-lg leading-relaxed max-w-2xl">{svc.long_description}</p>

            <div className="mt-8 grid sm:grid-cols-3 gap-3">
              <div className="p-4 rounded-xl bg-white border border-slate-200">
                <Clock className="w-4 h-4 text-[#1E3A8A]" />
                <div className="mt-2 text-xs uppercase tracking-widest text-slate-500">Processing</div>
                <div className="font-semibold text-[#0A192F] mt-0.5">{svc.processing_time}</div>
              </div>
              <div className="p-4 rounded-xl bg-white border border-slate-200">
                <Shield className="w-4 h-4 text-[#1E3A8A]" />
                <div className="mt-2 text-xs uppercase tracking-widest text-slate-500">Verified by</div>
                <div className="font-semibold text-[#0A192F] mt-0.5">CA Expert</div>
              </div>
              <div className="p-4 rounded-xl bg-white border border-slate-200">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                <div className="mt-2 text-xs uppercase tracking-widest text-slate-500">Delivery</div>
                <div className="font-semibold text-[#0A192F] mt-0.5">Digital + Email</div>
              </div>
            </div>

            {/* Plan cards below the info trio */}
            {hasPlans && (
              <div className="mt-10" data-testid="svc-plan-list">
                <div className="section-label">Choose your option</div>
                <div className="mt-3 grid sm:grid-cols-2 gap-3">
                  {sortedPlans.map((p) => {
                    const active = p.code === activePlan?.code;
                    const preview = isValidityPlan(p)
                      ? priceForPlan(p, availableYears(p)[0] || 1)
                      : Number(p.price || 0);
                    return (
                      <button
                        key={p.code}
                        type="button"
                        onClick={() => pickPlan(p)}
                        className={`relative text-left p-4 rounded-xl border transition-all ${
                          active
                            ? "border-[#1E3A8A] bg-[#1E3A8A]/5 ring-2 ring-[#1E3A8A]/30"
                            : "border-slate-200 bg-white hover:border-slate-300"
                        }`}
                        data-testid={`svc-plan-${p.code}`}
                      >
                        {p.popular && (
                          <span className="absolute -top-2 left-3 px-2 py-0.5 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-white text-[9px] font-bold uppercase tracking-widest shadow">
                            <Sparkles className="w-2.5 h-2.5 inline mr-0.5" /> Most Popular
                          </span>
                        )}
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="font-semibold text-[#0A192F]">{p.name}</div>
                            {p.description && <div className="text-xs text-slate-600 mt-1">{p.description}</div>}
                            {isValidityPlan(p) && (
                              <div className="text-[10px] text-slate-500 mt-1">Validity based · 5 year options</div>
                            )}
                          </div>
                          <div className="text-right shrink-0">
                            <div className="font-display font-bold text-lg text-[#0A192F]">
                              {isValidityPlan(p) ? "from " : ""}₹{preview}
                            </div>
                          </div>
                        </div>
                        {active && (
                          <div className="mt-3 flex items-center gap-1.5 text-xs text-[#1E3A8A] font-medium">
                            <CheckCircle2 className="w-3.5 h-3.5" /> Selected
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Year radios below plan cards — validity only */}
                {activeIsValidity && (
                  <YearRadios
                    plan={activePlan}
                    year={selectedYear}
                    onChange={setSelectedYear}
                    testidPrefix="svc-left-year"
                  />
                )}
                {/* Quantity selector — when enabled on the active plan */}
                {qtyEnabled && (
                  <div className="mt-4" data-testid="svc-left-qty-wrap">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Quantity</div>
                    <div className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white overflow-hidden">
                      <button
                        type="button"
                        onClick={() => setQuantity((q) => Math.max(1, (Number(q) || 1) - 1))}
                        className="px-3 py-2 text-sm text-slate-700 hover:bg-slate-100"
                        data-testid="svc-qty-decr"
                      >−</button>
                      <input
                        type="number"
                        min="1"
                        max="99"
                        value={quantity}
                        onChange={(e) => {
                          const n = parseInt(e.target.value || "1", 10);
                          setQuantity(Number.isFinite(n) ? Math.max(1, Math.min(99, n)) : 1);
                        }}
                        className="w-16 text-center text-sm font-semibold outline-none border-x border-slate-300 py-2"
                        data-testid="svc-qty-input"
                      />
                      <button
                        type="button"
                        onClick={() => setQuantity((q) => Math.min(99, (Number(q) || 1) + 1))}
                        className="px-3 py-2 text-sm text-slate-700 hover:bg-slate-100"
                        data-testid="svc-qty-incr"
                      >+</button>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-1">
                      ₹{Number(unitPrice).toLocaleString("en-IN")} × {effectiveQty} = ₹{Number(totalPrice).toLocaleString("en-IN")}
                    </div>
                  </div>
                )}
                {svc.pricing_note && (
                  <div className="text-xs text-slate-500 mt-3" data-testid="svc-pricing-note-left">{svc.pricing_note}</div>
                )}
              </div>
            )}
          </div>

          {/* RIGHT STICKY SIDEBAR */}
          <div className="lg:col-span-4 lg:sticky lg:top-24">
            <div className="rounded-2xl bg-white border border-slate-200 p-6 shadow-sm" data-testid="svc-pricing-card">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">
                {activePlan ? "Selected Plan" : "Total Fee"}
              </div>
              {/* Plan name — proper title case, prominent */}
              <div
                className="mt-1 font-display font-semibold text-xl text-[#0A192F]"
                data-testid="svc-side-plan-name"
              >
                {activePlan ? toTitleCase(activePlan.name) : svc.name}
              </div>
              {/* Price */}
              <div className="mt-2 font-display font-bold text-3xl text-[#0A192F]" data-testid="svc-total-price">
                ₹{Number(totalPrice).toLocaleString("en-IN")}
              </div>
              {activeIsValidity && (
                <div className="text-xs text-slate-500 mt-0.5">Year {selectedYear} validity</div>
              )}
              {qtyEnabled && effectiveQty > 1 && (
                <div className="text-xs text-slate-500 mt-0.5" data-testid="svc-side-qty-line">
                  ₹{Number(unitPrice).toLocaleString("en-IN")} × {effectiveQty}
                </div>
              )}
              {/* Short plan description */}
              {activePlan?.description && (
                <div
                  className="mt-2 text-sm text-slate-600 leading-relaxed"
                  data-testid="svc-side-plan-desc"
                >
                  {activePlan.description}
                </div>
              )}
              {svc.pricing_note && (
                <div className="text-[11px] text-slate-500 mt-2" data-testid="svc-pricing-note">{svc.pricing_note}</div>
              )}

              {/* Year radios in sidebar — validity only, above Apply Now */}
              {activeIsValidity && (
                <YearRadios
                  plan={activePlan}
                  year={selectedYear}
                  onChange={setSelectedYear}
                  testidPrefix="svc-side-year"
                />
              )}

              <Link to={applyHref}>
                <Button className="w-full mt-5 bg-[#1E3A8A] hover:bg-[#1E40AF] h-11" data-testid="svc-apply-now-btn">
                  Apply Now
                </Button>
              </Link>

              <div className="mt-5 space-y-1.5 text-sm text-slate-600">
                {(svc.features || []).map((f) => (
                  <div key={f} className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /> {f}</div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="section-label">Documents needed</div>
          <h2 className="mt-2 font-display font-bold text-2xl sm:text-3xl text-[#0A192F]">Please keep these ready</h2>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {(svc.required_documents || []).map((d, i) => (
              <div key={d} className="p-4 rounded-xl border border-slate-200 bg-slate-50/50 flex items-start gap-3">
                <span className="w-7 h-7 shrink-0 rounded-full bg-white border border-slate-200 flex items-center justify-center text-xs font-semibold text-[#1E3A8A]">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div className="text-sm font-medium text-[#0A192F]">{d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
